
import {
    activePageActionTypes,
    bloodRelationshipActionTypes,
    cacheActionTypes,
    commonActionTypes,
    countiesActionTypes,
    countriesActionTypes,
    dataActionTypes,
    dataStructureActionTypes,
    disabledUiActionTypes,
    enrollmentsActionTypes,
    exceptionActionTypes,
    flowActionTypes,
    formActionTypes,
    hyperReviewActionTypes,
    languageActionTypes,
    launchActionTypes,
    linkFromTypes,
    methodsActionTypes,
    memberAffordabilityTypes,
    adjustedFlowActionTypes,
    scrollActionTypes,
    switchDirectionsActionTypes,
    previousDataActionTypes,
    tribesActionTypes,
    ssapSagaActionTypes,
    summaryActionTypes
} from '@constants/actionTypes/ssapActionTypes';

// Sync action creators

/**
 * Initializes (hydrates) the SSAP store in a single dispatch.
 *
 * Dispatched once by `initialBootstrapSaga` after the application data is
 * fetched and processed. The single INITIALIZE_SSAP action fans out to many
 * SSAP reducers, each of which reads the slice of `payload` it owns:
 *
 *   - settings                 -> settingsReducer
 *   - dataStructure            -> structureReducer
 *   - sessionData              -> dataReducer / dataSnapshotReducer
 *   - isInReviewMode           -> launchReducer
 *   - isExtraFinalSummaryShown -> summaryReducer
 *   - page                     -> activePageReducer
 *   - direction                -> directionReducer
 *   - exception                -> exceptionReducer
 *
 * @param {Object} payload - Combined initial state for all SSAP slices.
 */
export const initializeSsap = (payload) => ({
    type: commonActionTypes.INITIALIZE_SSAP,
    payload
});

export const changeData = newState => ({
    type: dataActionTypes.CHANGE_DATA,
    newState
});

export const changeDirection = direction => ({
    type: switchDirectionsActionTypes.CHANGE_DIRECTION,
    direction
});

export const changeInputStatus = (input, formName) => ({
    type: formActionTypes.CHANGE_INPUT_STATUS,
    input,
    formName
});

export const changePage = (page, direction = undefined) => dispatch => {
    switch(page) {
        case 'increase':
            dispatch(changeDirection(direction ? direction : 'increase')); break;
        case 'decrease':
            dispatch(changeDirection(direction ? direction : 'decrease')); break;
        default:
            dispatch(changeDirection(direction ? direction : 'exact')); break;
    }
    return dispatch({
        type: activePageActionTypes.CHANGE_PAGE,
        page
    });
};

export const adjustFlow = (value, isEqual = false) => ({
    type: adjustedFlowActionTypes.ADJUST_FLOW,
    isEqual,
    value
});

export const clearDisabledUi = () => ({
    type: disabledUiActionTypes.CLEAR_DISABLED_UI
});

export const clearFormSubmission = formName => ({
    type: formActionTypes.CLEAR_SUBMISSION,
    formName
});

export const clearMethods = owner => ({
    type: methodsActionTypes.CLEAR_METHODS,
    owner
});

export const clearScrollToElement = () => ({
    type: scrollActionTypes.CLEAR_ELEMENT_FOR_SCROLLING
});

export const clearSsapState = () => ({
    type: commonActionTypes.CLEAR_SSAP_STATE
});

export const clearSsapSummary = () => ({
    type: summaryActionTypes.CLEAR_SUMMARY_VALUES
});

export const clearPreviousData = () => ({
    type: previousDataActionTypes.CLEAR_PREVIOUS_DATA
});

export const deleteForm = formName => ({
    type: formActionTypes.REMOVE_FORM,
    formName
});

export const registerForm = formName => ({
    type: formActionTypes.REGISTER_FORM,
    formName
});

export const makeFormSnapshot = (formName, snapshot) => ({
    type: formActionTypes.MAKE_SNAPSHOT,
    formName,
    snapshot
});

export const registerInput = (input, formName) => ({
    type: formActionTypes.REGISTER_INPUT,
    input,
    formName
});

export const reRegisterInput = (input, formName) => ({
    type: formActionTypes.RE_REGISTER_INPUT,
    input,
    formName
});

export const removeException = () => ({
    type: exceptionActionTypes.REMOVE_EXCEPTION,
});

export const removeInput = (input, formName) => ({
    type: formActionTypes.REMOVE_INPUT,
    input,
    formName
});

export const scrollToElement = element => ({
    type: scrollActionTypes.DEFINE_ELEMENT_FOR_SCROLLING,
    element
});

export const setBloodRelationships = bloodRelationships => ({
    type: bloodRelationshipActionTypes.SET_RELATIONS,
    bloodRelationships
});

export const setBloodRelationshipsWholeList = bloodRelationships => ({
    type: bloodRelationshipActionTypes.SET_RELATIONS_WHOLE_LIST,
    bloodRelationships
});

export const setCounty = (payload, pageName) => ({
    type: countiesActionTypes.SET_COUNTY_FOUND_BY_STATE_AND_ZIPCODE,
    payload,
    pageName
});

export const setCountries = countries => ({
    type: countriesActionTypes.SET_COUNTRIES,
    countries
});

export const setData = data => ({
    type: dataActionTypes.SET_DATA,
    data
});

export const setSessionInitialData = data => ({
    type: dataActionTypes.SET_SESSION_INITIAL_DATA,
    data
});

export const setPreviousData = data => ({
    type: previousDataActionTypes.SET_PREVIOUS_DATA,
    data
});

export const updatePreviousSSNs = ssns => ({
    type: previousDataActionTypes.UPDATE_PREVIOUS_DATA_SSNS,
    ssns
});

export const updateSSNs = (ssns, isFromCache = false) => ({
    type: dataActionTypes.UPDATE_SSNS,
    isFromCache,
    ssns
});

export const removeCachedData = applicantGuid => ({
    type: cacheActionTypes.REMOVE_CACHED_DATA,
    applicantGuid
});

export const updateCachedSSN = (applicantGuid, ssn) => ({
    type: cacheActionTypes.UPDATE_CACHED_SSN,
    applicantGuid,
    ssn
});

export const cacheSSNs = ssns => ({
    type: cacheActionTypes.CACHE_SSNS,
    ssns
});

export const cachePreviousSSNs = ssns => ({
    type: cacheActionTypes.CACHE_PREVIOUS_SSNS,
    ssns
});

export const updatePreviousIMI = imi => ({
    type: previousDataActionTypes.UPDATE_PREVIOUS_DATA_IMI,
    imi
});

export const updateIMI = (imi, isFromCache = false) => ({
    type: dataActionTypes.UPDATE_IMI,
    isFromCache,
    imi
});

export const cacheIMI = imi => ({
    type: cacheActionTypes.CACHE_IMI,
    imi
});

export const updateCachedIMI = (applicantGuid, imi) => ({
    type: cacheActionTypes.UPDATE_CACHED_IMI,
    applicantGuid,
    imi
});

export const cachePreviousIMI = imi => ({
    type: cacheActionTypes.CACHE_PREVIOUS_IMI,
    imi
});

export const setDataStructure = update => ({
    type: dataStructureActionTypes.SET_DATA_STRUCTURE,
    update
});

export const setMemberAffordabilty = affordabilityData => ({
    type: memberAffordabilityTypes.SET_MEMBER_AFFORDABILITY,
    affordabilityData
});

export const setException = (exceptionType, exceptionPages, shouldChangePage = undefined) => dispatch => {
    if(shouldChangePage === true) {
        dispatch(changePage(exceptionPages[0], 'increase'));
    }
    if(Number.isInteger(shouldChangePage)) {
        dispatch(changePage(shouldChangePage, 'increase'));
    }
    return dispatch({
        type: exceptionActionTypes.SET_EXCEPTION,
        exceptionType,
        exceptionPages
    });
};

export const setFlow = flowType => ({
    type: flowActionTypes.SET_FLOW,
    flowType
});

export const setLanguage = language => ({
    type: languageActionTypes.CHANGE_LANGUAGE,
    language
});

export const setLinkFrom = linkFrom => ({
    type: linkFromTypes.SET_LINK_FROM,
    linkFrom
});

export const setMethods = (owner, methods) => ({
    type: methodsActionTypes.SET_METHODS,
    owner,
    methods
});


export const setTribes = tribes => ({
    type: tribesActionTypes.SET_TRIBES,
    tribes
});

export const submitForm = formName => ({
    type: formActionTypes.SUBMIT_FORM,
    formName
});

export const switchHyperReview = isHyperReview => ({
    type: hyperReviewActionTypes.SET_HYPER_REVIEW,
    isHyperReview
});

export const toggleUpdatedFieldsVisibility = areVisible => ({
    type: summaryActionTypes.TOGGLE_UPDATED_VALUES_VISIBILITY,
    areVisible
});

export const updateDisabledUi = disabledUi => ({
    type: disabledUiActionTypes.UPDATE_DISABLED_UI,
    disabledUi
});

export const updateException = exceptionPages => ({
    type: exceptionActionTypes.UPDATE_EXCEPTION,
    exceptionPages
});

export const validateForm = (payload, formName) => ({
    type: formActionTypes.VALIDATE_FORM,
    payload,
    formName
});

export const setSsapSummaryCrashed = () => ({
    type: summaryActionTypes.SET_SUMMARY_CRASHED
});

export const showExtraFinalSummary = isShown => ({
    type: summaryActionTypes.SHOW_EXTRA_FINAL_SUMMARY,
    isShown
});

export const setBulkEnrollments = enrollments => ({
    type: enrollmentsActionTypes.SET_BULK_ENROLLMENTS,
    enrollments
});

export const fetchEnrollmentsProgress = isDataFetched => ({
    type: enrollmentsActionTypes.SET_PROGRESS,
    isDataFetched
});

export const setEnrollment = (applicantGuid, isEnrolled) => ({
    type: enrollmentsActionTypes.SET_ENROLLMENT,
    applicantGuid,
    isEnrolled
});

export const setSsapLaunch = launchConfig => ({
    type: launchActionTypes.SET_SSAP_LAUNCH,
    launchConfig
});

// Consumed by sagas

export const checkBloodRelationships = callback => ({
    type: ssapSagaActionTypes.BLOOD_RELATIONSHIPS_CHECK,
    callback
});

export const checkBloodRelationshipsFailure = () => ({
    type: ssapSagaActionTypes.BLOOD_RELATIONSHIPS_CHECK_FAILURE
});

export const checkBloodRelationshipsSuccess = () => ({
    type: ssapSagaActionTypes.BLOOD_RELATIONSHIPS_CHECK_SUCCESS
});

export const fetchAffordability = () => ({
    type: ssapSagaActionTypes.FETCH_AFFORDABILITY
});

export const fetchAffordabilityFailure = () => ({
    type: ssapSagaActionTypes.FETCH_AFFORDABILITY_FAILURE
});

export const fetchAffordabilitySuccess = () => ({
    type: ssapSagaActionTypes.FETCH_AFFORDABILITY_SUCCESS
});

export const fetchAllBloodRelationships = () => ({
    type: ssapSagaActionTypes.FETCH_ALL_BLOOD_RELATIONSHIPS
});

export const fetchAllBloodRelationshipsFailure = () => ({
    type: ssapSagaActionTypes.FETCH_ALL_BLOOD_RELATIONSHIPS_FAILURE
});

export const fetchAllBloodRelationshipsSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_ALL_BLOOD_RELATIONSHIPS_SUCCESS
});

export const fetchApplication = () => ({
    type: ssapSagaActionTypes.FETCH_SSAP_DATA
});

export const fetchApplicationFailure = () => ({
    type: ssapSagaActionTypes.FETCH_SSAP_DATA_FAILURE
});

export const fetchApplicationSilentFailure = () => ({
    type: ssapSagaActionTypes.FETCH_SSAP_DATA_SILENT_FAILURE
});

export const fetchApplicationSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_SSAP_DATA_SUCCESS
});

export const fetchPreviousApplication = currentPCPApplicantGuid => ({
    type: ssapSagaActionTypes.FETCH_SSAP_PREVIOUS_DATA,
    currentPCPApplicantGuid
});

export const fetchPreviousApplicationFailure = () => ({
    type: ssapSagaActionTypes.FETCH_SSAP_PREVIOUS_DATA_FAILURE
});

export const fetchPreviousApplicationSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_SSAP_PREVIOUS_DATA_SUCCESS
});

export const fetchTribes = () => ({
    type: ssapSagaActionTypes.FETCH_TRIBES
});

export const fetchTribesFailure = () => ({
    type: ssapSagaActionTypes.FETCH_TRIBES_FAILURE
});

export const fetchTribesSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_TRIBES_SUCCESS
});

export const fetchCountries = () => ({
    type: ssapSagaActionTypes.FETCH_COUNTRIES
});

export const fetchCountriesFailure = () => ({
    type: ssapSagaActionTypes.FETCH_COUNTRIES_FAILURE
});

export const fetchCountriesSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_COUNTRIES_SUCCESS
});

export const fetchEnrollments = () => ({
    type: ssapSagaActionTypes.FETCH_ENROLLMENTS
});

export const fetchEnrollmentsFailure = () => ({
    type: ssapSagaActionTypes.FETCH_ENROLLMENTS_FAILURE
});

export const fetchEnrollmentsSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_ENROLLMENTS_SUCCESS
});


export const sendSummaryToInbox = () => ({
    type: ssapSagaActionTypes.SEND_SUMMARY_TO_INBOX
});

export const fetchCountiesForZipcode = (state, postalCode, pageName) => ({
    type: ssapSagaActionTypes.FETCH_COUNTIES_FOR_ZIPCODE,
    pageName,
    postalCode,
    state
});

export const fetchCountiesForZipcodeFailure = () => ({
    type: ssapSagaActionTypes.FETCH_COUNTIES_FOR_ZIPCODE_FAILURE
});

export const fetchCountiesForZipcodeSuccess = () => ({
    type: ssapSagaActionTypes.FETCH_COUNTIES_FOR_ZIPCODE_SUCCESS
});

export const receiveImmigrationInfo = callbacks => ({
    type: ssapSagaActionTypes.RECEIVE_IMMIGRATION_INFO,
    callbacks
});

export const receiveSSNs = callbacks => ({
    type: ssapSagaActionTypes.RECEIVE_SSNS,
    callbacks
});

export const startReceiveSSNs = () => ({
    type: ssapSagaActionTypes.RECEIVE_SSNS_START
});

export const receiveSSNsFailure = () => ({
    type: ssapSagaActionTypes.RECEIVE_SSNS_FAILURE
});

export const receiveSSNsSuccess = () => ({
    type: ssapSagaActionTypes.RECEIVE_SSNS_SUCCESS
});

export const startReceiveIMI = () => ({
    type: ssapSagaActionTypes.RECEIVE_IMI_START
});

export const receiveIMIFailure = () => ({
    type: ssapSagaActionTypes.RECEIVE_IMI_FAILURE
});

export const receiveIMISuccess = () => ({
    type: ssapSagaActionTypes.RECEIVE_IMI_SUCCESS
});

export const submitApplication = ssapData => ({
    type: ssapSagaActionTypes.SUBMIT_SSAP,
    ssapData
});

export const submitApplicationFailure = () => ({
    type: ssapSagaActionTypes.SUBMIT_SSAP_FAILURE
});

export const submitApplicationSuccess = () => ({
    type: ssapSagaActionTypes.SUBMIT_SSAP_SUCCESS
});

export const saveSsap = (callbacks, ssapData) => ({
    type: ssapSagaActionTypes.SAVE_SSAP,
    callbacks,
    ssapData
});

export const saveSsapFailure = () => ({
    type: ssapSagaActionTypes.SAVE_SSAP_FAILURE
});

export const saveSsapSuccess = () => ({
    type: ssapSagaActionTypes.SAVE_SSAP_SUCCESS
});

export const validateAddress = (callback, address, stateCode) => ({
    type: ssapSagaActionTypes.VALIDATE_ADDRESS,
    callback,
    address,
    stateCode
});

export const validateAddressFailure = () => ({
    type: ssapSagaActionTypes.VALIDATE_ADDRESS_FAILURE
});

export const validateAddressSuccess = () => ({
    type: ssapSagaActionTypes.VALIDATE_ADDRESS_SUCCESS
});

export const setSsapInReviewMode = () => ({
    type: launchActionTypes.SET_SSAP_IN_REVIEW_MODE
});